import { CSSProperties, useEffect, useMemo, useRef } from "react";
import { classNames } from "@ct-react/core";
import { Asset } from "@shared/graphql";
import { Ratio } from "../../models/common";
import "./ratio-image.scss";

type SetType = "SM" | "MD" | "LG" | "FULL";
type MappedSize = Record<SetType, number>;
type Sources = Record<SetType, string>;

type Props = {
  source: Asset[] | string;
  alt?: string;
  className?: string;
  ratio?: Ratio;
  lazy?: boolean;
}

const limits: MappedSize = { "SM": 400, "MD": 800, "LG": 1200, "FULL": 4000 };
const toSrcSet = (sources: Sources) => Object.entries(sources)
                                             .map(([ type, url ]) => `${url} ${limits[type as SetType]}w`)
                                             .join(", ");

const RatioImage = (
  {
    className,
    ratio = "3/2",
    lazy = true,
    alt = "",
    source
  }: Props) => {

  const imgRef = useRef<HTMLImageElement>(null);

  const sources = useMemo(() => {
    if (typeof source === "string")
      return { "FULL": source } as Sources;
    return source.reduce((acc, asset) => ({ ...acc, [asset.breakpoint || "FULL"]: asset.url }), {} as Sources);
  }, [ source ]);

  useEffect(() => {
    if (!imgRef.current || !lazy) return;
    const observer = new IntersectionObserver((entries, obs) => entries.forEach((entry) => {
      if (!imgRef.current || !entry.isIntersecting) return;
      imgRef.current.srcset = imgRef.current.dataset.srcset!;
      imgRef.current.sizes = imgRef.current.dataset.sizes!;
      imgRef.current.src = imgRef.current.dataset.src!;
      imgRef.current.removeAttribute("data-srcSet");
      imgRef.current.removeAttribute("data-sizes");
      imgRef.current.removeAttribute("data-src");
      obs.unobserve(imgRef.current);
    }));
    observer.observe(imgRef.current);
    return () => {
      observer.disconnect();
    };
  }, []);

  const wrapperClasses = classNames("rimg", className);

  return (
    <picture className={wrapperClasses}
             style={{ "--ccv-img-ratio": ratio } as CSSProperties}>
      <img ref={imgRef}
           {...{
             [lazy ? "data-srcset" : "srcSet"]: toSrcSet(sources),
             [lazy ? "data-sizes": "sizes"]: "(max-width: 768px) 100vw, (max-width: 1400px) 50vw, 33vw",
             [lazy ? "data-src" : "src"]: sources["MD"] || sources["LG"] || sources["SM"],
           }}
           alt={alt} />
    </picture>);

}

export default RatioImage;
